/**
 * Utility functions for sending emails
 */
import { genericSupabase } from "@/integrations/supabase/client";

// Helper to get the base URL for APIs
const getApiBaseUrl = (): string => {
  // For development, use the Vite proxy
  if (import.meta.env.DEV) {
    return '';
  }
  // For production, use environment variable or default
  return import.meta.env.VITE_API_BASE_URL || '';
};

const getAppBaseUrl = (): string => {
  // Use window.location for current app URL
  return window.location.origin;
};

/**
 * Sends an email notification using the PHP backend API
 * @param email The recipient's email address
 * @param leadId The lead ID
 * @param leadDetails The lead details object
 * @param partnerId Optional partner ID for tracking
 * @returns Promise with the email response
 */
export const sendEmailNotification = async (
  email: string,
  leadId: string,
  leadDetails: {
    name: string;
    service: string;
    phone: string;
    address: string;
    timeframe: string;
    comments?: string;
  },
  partnerId?: string,
  emailType: 'partner_notification' | 'admin_unassigned' = 'partner_notification'
): Promise<{ status: string; message?: string; error?: string }> => {
  try {
    // Generate a unique tracking ID for this email
    const trackingId = `${leadId}-${Date.now()}`;
    
    // Call the PHP API to send the email
    const response = await fetch(`${getApiBaseUrl()}/send-email.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email,
        leadId,
        leadDetails,
        trackingId,
        partnerId,
        emailType
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    
    // Log email send event locally instead of directly to Supabase
    try {
      // Store in localStorage for fallback/development
      const storageKey = 'email-logs';
      const existingLogs = JSON.parse(localStorage.getItem(storageKey) || '[]');
      
      existingLogs.push({
        trackingId,
        leadId,
        email,
        event: 'sent',
        partnerId,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent
      });
      
      localStorage.setItem(storageKey, JSON.stringify(existingLogs));
      
      console.log('Email log stored locally:', {
        trackingId,
        leadId,
        email,
        event: 'sent',
        partnerId,
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      console.error('Error storing email log locally:', error);
    }
    
    return {
      status: result.status === 'success' ? 'success' : 'failed',
      message: result.message,
      error: result.error
    };
  } catch (error) {
    console.error('Error sending email:', error);
    return {
      status: 'failed',
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

/**
 * Track email open event
 * @param trackingId The email tracking ID
 * @param leadId The lead ID
 * @param partnerId Optional partner ID
 * @returns Promise with tracking status
 */
export const trackEmailOpen = async (
  trackingId: string,
  leadId: string,
  partnerId?: string
): Promise<{ status: string; message?: string; error?: string }> => {
  return await logEmailEvent(trackingId, leadId, undefined, 'open', partnerId);
};

/**
 * Track email click event
 * @param trackingId The email tracking ID
 * @param leadId The lead ID
 * @param buttonType The type of button clicked
 * @param partnerId Optional partner ID
 * @returns Promise with tracking status
 */
export const trackEmailClick = async (
  trackingId: string,
  leadId: string,
  buttonType: 'view' | 'status-pending' | 'status-completed' | 'status-info',
  partnerId?: string
): Promise<{ status: string; message?: string; error?: string }> => {
  return await logEmailEvent(trackingId, leadId, undefined, `click-${buttonType}`, partnerId);
};

/**
 * Create trackable URL for email links
 * @param baseUrl The base URL for the link
 * @param trackingId The tracking ID
 * @param leadId The lead ID
 * @param clickType The type of click to track
 * @param partnerId Optional partner ID
 * @returns Trackable URL with tracking parameters
 */
export const createTrackableUrl = (
  baseUrl: string, 
  trackingId: string, 
  leadId: string, 
  clickType: 'view' | 'status-pending' | 'status-completed' | 'status-info',
  partnerId?: string
): string => {
  // Build URL with tracking parameters
  const trackingUrl = new URL(`${getAppBaseUrl()}/api/track-email.php`);
  trackingUrl.searchParams.append('trackingId', trackingId);
  trackingUrl.searchParams.append('leadId', leadId);
  trackingUrl.searchParams.append('event', `click-${clickType}`);
  trackingUrl.searchParams.append('destination', encodeURIComponent(baseUrl));
  if (partnerId) trackingUrl.searchParams.append('partnerId', partnerId);
  
  return trackingUrl.toString();
};

/**
 * Log email events (sent, open, click)
 * @param trackingId The tracking ID
 * @param leadId The lead ID
 * @param email Optional email address
 * @param event The event type
 * @param partnerId Optional partner ID
 * @returns Promise with the logging status
 */
async function logEmailEvent(
  trackingId: string,
  leadId: string,
  email?: string,
  event: 'sent' | 'open' | 'click' | string = 'sent',
  partnerId?: string
): Promise<{ status: string; message?: string; error?: string }> {
  try {
    const logData = {
      trackingId,
      leadId,
      email,
      event,
      partnerId,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent
    };

    // Store in localStorage for fallback/development
    const storageKey = 'email-logs';
    const existingLogs = JSON.parse(localStorage.getItem(storageKey) || '[]');
    
    existingLogs.push(logData);
    
    localStorage.setItem(storageKey, JSON.stringify(existingLogs));
    
    // Record via the API as well
    try {
      const response = await fetch(`${getApiBaseUrl()}/track-email.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(logData),
      });
      
      if (!response.ok) {
        console.warn('Warning: Failed to record email event via API');
      }
    } catch (apiError) {
      console.warn('Warning: Error calling email tracking API:', apiError);
    }
    
    return {
      status: 'success',
      message: `Email event ${event} logged successfully`
    };
  } catch (error) {
    console.error('Error logging email event:', error);
    return {
      status: 'failed',
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

/**
 * Creates a lead notification email subject with emoji
 * @returns Formatted email subject
 */
export const createLeadEmailSubject = (): string => {
  return "🔔 Νέα Επαφή Ενδιαφερομένου - Άμεση Ενέργεια";
};

/**
 * Generate HTML email template with modern design and tracking pixel
 * This function is used in the PHP email sender backend
 * It's kept here for reference only
 */
export function generateEmailTemplate(
  leadDetails: {
    name: string;
    service: string;
    phone: string;
    address: string;
    timeframe: string;
    comments?: string;
  },
  leadId: string,
  trackingId: string,
  partnerId?: string
): string {
  // Create button URLs for tracking clicks
  const viewLeadUrl = createTrackableUrl(
    `${getAppBaseUrl()}/lead/${leadId}`,
    trackingId,
    leadId,
    'view',
    partnerId
  );
  
  const markPendingUrl = createTrackableUrl(
    `${getAppBaseUrl()}/lead/${leadId}?status=pending`,
    trackingId,
    leadId,
    'status-pending',
    partnerId
  );
  
  const markCompletedUrl = createTrackableUrl(
    `${getAppBaseUrl()}/lead/${leadId}?status=completed`,
    trackingId,
    leadId,
    'status-completed',
    partnerId
  );
  
  const infoUrl = createTrackableUrl(
    `${getAppBaseUrl()}/info`,
    trackingId,
    leadId,
    'status-info',
    partnerId
  );

  // Generate the enhanced email template HTML with better styling and modern design
  const template = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>🔔 Νέα Επαφή Ενδιαφερομένου</title>
      <style>
        body { 
          font-family: 'Helvetica Neue', Arial, sans-serif; 
          line-height: 1.6; 
          color: #333; 
          margin: 0; 
          padding: 0; 
          background-color: #f9f9f9;
        }
        .container { 
          max-width: 600px; 
          margin: 0 auto; 
          padding: 0;
          background-color: #fff;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 4px 6px rgba(0,0,0,0.05);
        }
        .header { 
          background: linear-gradient(135deg, #8E69AB 0%, #6E59A5 100%);
          color: white; 
          padding: 30px 20px; 
          text-align: center; 
        }
        .content { 
          padding: 30px 25px; 
          background-color: #fff; 
        }
        .footer { 
          background-color: #f8f9fa; 
          padding: 20px; 
          text-align: center; 
          font-size: 12px; 
          color: #6c757d; 
          border-top: 1px solid #eee;
        }
        .lead-details { 
          background-color: #f8f9fa; 
          padding: 20px; 
          border-radius: 6px; 
          margin: 25px 0; 
          border-left: 4px solid #6E59A5;
        }
        .detail-row { 
          margin-bottom: 12px;
          display: flex;
        }
        .detail-label { 
          font-weight: bold;
          min-width: 120px;
        }
        .detail-value {
          flex: 1;
        }
        .bell-icon { 
          font-size: 40px; 
          margin-bottom: 15px; 
        }
        .btn { 
          display: inline-block; 
          padding: 12px 24px; 
          margin: 10px 5px; 
          border-radius: 30px; 
          text-decoration: none; 
          color: white; 
          font-weight: 500; 
          text-align: center;
          transition: all 0.3s ease;
          font-size: 14px;
          box-shadow: 0 2px 5px rgba(0,0,0,0.1);
        }
        .btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(0,0,0,0.15);
        }
        .btn-primary { 
          background: linear-gradient(135deg, #8E69AB 0%, #6E59A5 100%);
        }
        .btn-success { 
          background: linear-gradient(135deg, #20c997 0%, #0ca678 100%);
        }
        .btn-info { 
          background: linear-gradient(135deg, #4dabf7 0%, #228be6 100%);
        }
        .call-now { 
          background: linear-gradient(135deg, #fa5252 0%, #e03131 100%);
          margin-top: 10px;
          margin-bottom: 25px;
          padding: 14px 28px;
          font-size: 16px;
        }
        .call-now-icon { 
          margin-right: 8px; 
        }
        .actions { 
          text-align: center; 
          margin: 30px 0; 
        }
        h1 {
          margin-top: 10px;
          font-weight: 600;
          font-size: 26px;
        }
        .section-title {
          font-size: 18px;
          font-weight: 600;
          margin-bottom: 15px;
          color: #6E59A5;
        }
        p {
          margin: 16px 0;
          font-size: 16px;
          color: #4a4a4a;
        }
        @media (max-width: 600px) {
          .btn {
            display: block;
            width: 100%;
            margin: 10px 0;
          }
        }
      </style>
    </head>
    <body>
      <div class="container">
        <div class="header">
          <div class="bell-icon">🔔</div>
          <h1>Νέα Επαφή Ενδιαφερομένου</h1>
        </div>
        <div class="content">
          <p>Αγαπητέ συνεργάτη,</p>
          <p>Έχετε λάβει μια νέα επαφή ενδιαφερομένου που ταιριάζει με τις υπηρεσίες που προσφέρετε.</p>
          
          <div class="section-title">📋 Στοιχεία Ενδιαφερομένου</div>
          <div class="lead-details">
            <div class="detail-row">
              <span class="detail-label">👤 Όνομα:</span>
              <span class="detail-value">${leadDetails.name}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">📞 Τηλέφωνο:</span>
              <span class="detail-value">${leadDetails.phone}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">📍 Διεύθυνση:</span>
              <span class="detail-value">${leadDetails.address}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">🛠️ Υπηρεσία:</span>
              <span class="detail-value">${leadDetails.service}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">⏱️ Χρονικό Πλαίσιο:</span>
              <span class="detail-value">${leadDetails.timeframe}</span>
            </div>
            ${leadDetails.comments ? `
            <div class="detail-row">
              <span class="detail-label">💬 Σχόλια:</span>
              <span class="detail-value">${leadDetails.comments}</span>
            </div>` : ''}
          </div>
          
          <p>Παρακαλούμε <strong>επικοινωνήστε το συντομότερο δυνατό</strong> με τον ενδιαφερόμενο - οι γρήγορες απαντήσεις αυξάνουν σημαντικά τις πιθανότητες σύναψης συνεργασίας.</p>
          
          <div class="actions">
            <a href="tel:${leadDetails.phone}" class="btn call-now">📞 Κάλεσε Τώρα</a>
          </div>
          
          <div class="section-title">🔄 Ενημέρωση Κατάστασης</div>
          <p>Μπορείτε να ενημερώσετε την κατάσταση της επαφής μέσω των παρακάτω συνδέσμων:</p>
          
          <div class="actions">
            <a href="${viewLeadUrl}" class="btn btn-primary">👁️ Προβολή Στοιχείων</a>
            <a href="${markPendingUrl}" class="btn btn-info">⏳ Αναλήφθηκε</a>
            <a href="${markCompletedUrl}" class="btn btn-success">✅ Ολοκληρώθηκε</a>
          </div>
          
          <p>Μπορείτε να παρακολουθείτε όλες τις επαφές σας στην πλατφόρμα μας.</p>
        </div>
        <div class="footer">
          <p>© ${new Date().getFullYear()} Διαθερμική. Με επιφύλαξη παντός δικαιώματος.</p>
          <p><a href="${infoUrl}" style="color: #6c757d; text-decoration: underline;">Περισσότερες Πληροφορίες</a></p>
        </div>
      </div>
      <img src="${getAppBaseUrl()}/api/track-email.php?trackingId=${trackingId}&leadId=${leadId}&partnerId=${partnerId || ''}" alt="" width="1" height="1" style="display:none;" />
    </body>
    </html>
  `;

  return template;
}

/**
 * Generate an unassigned lead notification email for admin
 */
export function generateUnassignedLeadEmail(
  leadDetails: {
    name: string;
    service: string;
    phone: string;
    address: string;
    timeframe: string;
    comments?: string;
  },
  leadId: string,
  trackingId: string
): string {
  const viewLeadUrl = `${getAppBaseUrl()}/admin/lead/${leadId}`;
  
  const template = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>⚠️ Μη Ανατεθειμένη Επαφή</title>
      <style>
        body { 
          font-family: 'Helvetica Neue', Arial, sans-serif; 
          line-height: 1.6; 
          color: #333; 
          margin: 0; 
          padding: 0; 
          background-color: #f9f9f9;
        }
        .container { 
          max-width: 600px; 
          margin: 0 auto; 
          padding: 0;
          background-color: #fff;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 4px 6px rgba(0,0,0,0.05);
        }
        .header { 
          background: linear-gradient(135deg, #ff9b26 0%, #ff7730 100%);
          color: white; 
          padding: 30px 20px; 
          text-align: center; 
        }
        .content { 
          padding: 30px 25px; 
          background-color: #fff; 
        }
        .footer { 
          background-color: #f8f9fa; 
          padding: 20px; 
          text-align: center; 
          font-size: 12px; 
          color: #6c757d; 
          border-top: 1px solid #eee;
        }
        .lead-details { 
          background-color: #f8f9fa; 
          padding: 20px; 
          border-radius: 6px; 
          margin: 25px 0; 
          border-left: 4px solid #ff7730;
        }
        .detail-row { 
          margin-bottom: 12px;
          display: flex;
        }
        .detail-label { 
          font-weight: bold;
          min-width: 120px;
        }
        .detail-value {
          flex: 1;
        }
        .warning-icon { 
          font-size: 40px; 
          margin-bottom: 15px; 
        }
        .btn { 
          display: inline-block; 
          padding: 12px 24px; 
          margin: 10px 5px; 
          border-radius: 30px; 
          text-decoration: none; 
          color: white; 
          font-weight: 500; 
          text-align: center;
          transition: all 0.3s ease;
          font-size: 14px;
          box-shadow: 0 2px 5px rgba(0,0,0,0.1);
        }
        .btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(0,0,0,0.15);
        }
        .btn-warning { 
          background: linear-gradient(135deg, #ff9b26 0%, #ff7730 100%);
        }
        .actions { 
          text-align: center; 
          margin: 30px 0; 
        }
        h1 {
          margin-top: 10px;
          font-weight: 600;
          font-size: 26px;
        }
        .section-title {
          font-size: 18px;
          font-weight: 600;
          margin-bottom: 15px;
          color: #ff7730;
        }
        p {
          margin: 16px 0;
          font-size: 16px;
          color: #4a4a4a;
        }
        @media (max-width: 600px) {
          .btn {
            display: block;
            width: 100%;
            margin: 10px 0;
          }
        }
      </style>
    </head>
    <body>
      <div class="container">
        <div class="header">
          <div class="warning-icon">⚠️</div>
          <h1>Μη Ανατεθειμένη Επαφή</h1>
        </div>
        <div class="content">
          <p>Αγαπητέ διαχειριστή,</p>
          <p>Υπάρχει μια νέα επαφή ενδιαφερομένου που <strong>δεν μπορεί να ανατεθεί</strong> σε κάποιον συνεργάτη αυτόματα.</p>
          
          <div class="section-title">📋 Στοιχεία Ενδιαφερομένου</div>
          <div class="lead-details">
            <div class="detail-row">
              <span class="detail-label">👤 Όνομα:</span>
              <span class="detail-value">${leadDetails.name}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">📞 Τηλέφωνο:</span>
              <span class="detail-value">${leadDetails.phone}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">📍 Διεύθυνση:</span>
              <span class="detail-value">${leadDetails.address}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">🛠️ Υπηρεσία:</span>
              <span class="detail-value">${leadDetails.service}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">⏱️ Χρονικό Πλαίσιο:</span>
              <span class="detail-value">${leadDetails.timeframe}</span>
            </div>
            ${leadDetails.comments ? `
            <div class="detail-row">
              <span class="detail-label">💬 Σχόλια:</span>
              <span class="detail-value">${leadDetails.comments}</span>
            </div>` : ''}
          </div>
          
          <p>Παρακαλούμε αναθέστε αυτή την επαφή σε έναν συνεργάτη ή επικοινωνήστε με τον ενδιαφερόμενο για περισσότερες πληροφορίες.</p>
          
          <div class="actions">
            <a href="${viewLeadUrl}" class="btn btn-warning">🔍 Διαχείριση Επαφής</a>
          </div>
        </div>
        <div class="footer">
          <p>© ${new Date().getFullYear()} Διαθερμική. Με επιφύλαξη παντός δικαιώματος.</p>
        </div>
      </div>
      <img src="${getAppBaseUrl()}/api/track-email.php?trackingId=${trackingId}&leadId=${leadId}" alt="" width="1" height="1" style="display:none;" />
    </body>
    </html>
  `;

  return template;
}

/**
 * Get email logs from the backend API
 * @param partnerId Optional partner ID filter
 * @param leadId Optional lead ID filter
 * @returns Promise with email logs array
 */
export const getEmailLogs = async (partnerId?: string, leadId?: string): Promise<any[]> => {
  try {
    let url = `${getApiBaseUrl()}/get-email-logs.php`;
    const params = new URLSearchParams();
    
    if (partnerId) params.append('partnerId', partnerId);
    if (leadId) params.append('leadId', leadId);
    
    if (params.toString()) {
      url = `${url}?${params.toString()}`;
    }
    
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching email logs:', error);
    return [];
  }
};
